/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@we-make-websites/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { isCdnUrl } from '~/helpers/general'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatProductPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    media: formatMedia(product),
    options: formatOptions(product),
    price: formatProductPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.type,
    compatibility: product.compatibility,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    variants: formatVariants(product),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))
  formattedProduct.featuredImage = featuredImages[0]

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}
export function formatProductt(response) {
  const product = response

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatProductPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    sku: product?.variants?.edges?.length > 0 ? product.variants.edges[0].node.sku : "N/A",
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    media: formatMedia(product),
    //options: formatOptions(product),
    price: formatProductPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.type,
    compatibility:product.compatibility_new?.value 
    ? product.compatibility_new?.value : product.compatibility_new,
    url: `https://${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    variants: formatVariants(product),
    vendor: product.vendor,
    featuredImage :product.featuredImages?.edges[0]?.node
  }

  /**
   * Add additional values which rely on formatted response.
   */
  let featuredImages = formatImages(product, transformEdges(product.featuredImages))
  //formattedProduct.featuredImage = featuredImages[0]

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}


/**
 * Formats product price.
 * @param {Object} price - Product price response.
 * @returns {Object}
 */
function formatProductPrice(price) {
  return {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} product - GraphQL response.
 * @param {Array} [images] - Custom array of images for formatting.
 * @returns {Array}
 */
function formatImages(product, images = false) {
  const imagesArray = images ? images : transformEdges(product?.images)

  if (!imagesArray.length) {
    return []
  }

  const formattedImages = imagesArray.map((image, index) => {
    if (!image.url) {
      return {}
    }

    const linkedVariant = product.variants?.length
      ? transformEdges(product.variants).find((variant) => {
        return variant.image.id === image.id
      })
      : false

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      index,
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })

  if (images.length === 1) {
    return formattedImages[0]
  }

  return formattedImages
}

/**
 * Formats media.
 * @param {Oject} product - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  const mediaArray = transformEdges(product.media)

  if (!mediaArray.length) {
    return []
  }

  return mediaArray.map((media, index) => {
    const formattedMedia = {
      alt: media.alt,
      id: decodeApiId(media.id) || false,
      index,
      mediaType: media.mediaType.toLowerCase(),
    }

    switch (formattedMedia.mediaType) {
      case 'external_video':
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.host = media.host
        formattedMedia.previewImage = formatImages(false, [media.previewImage])
        break

      case 'image':
        formattedMedia.image = formatImages(false, [media.image])
        break

      case 'model_3d':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            mimeType: source.mimeType,
            url: source.url,
          }
        })
        break

      case 'video':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            height: source.height,
            mimeType: source.mimeType,
            url: source.url,
            width: source.width,
          }
        })
        break
    }

    return formattedMedia
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatVariants(product) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: formatVariantPrice(variant.compareAtPrice),
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) ?? false,
      inventory: variant.quantityAvailable,
      price: formatVariantPrice(variant.price),
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift card variants show price as their title for localisation purposes.
     */
    formattedVariant.title = product.giftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats variant price.
 * @param {Object} price - Variant price response.
 * @returns {Object}
 */
function formatVariantPrice(price) {
  if (!price) {
    return {
      amount: false,
      currencyCode: false,
    }
  }

  return {
    amount: Number(price.amount) * 100,
    currencyCode: price.currencyCode,
  }
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
    },
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((product) => {
    return formatCollectionProduct(product, formattedCollection)
  })

  return formattedCollection
}

/**
 * Format collection product response from collection or recommendations.
 * @param {Object} product - GraphQL response.
 * @param {Object} [collection] - Formatted collection response.
 * @returns {Object}
 */
export function formatCollectionProduct(product, collection) {
  const url = collection
    ? `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`

  const formattedCollectionProduct = {
    available: product.available,
    compareAtPrice: formatProductPrice(product.compareAtPriceRange),
    handle: product.handle,
    price: formatProductPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.type,
    url,
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))
  formattedCollectionProduct.featuredImage = featuredImages[0]

  formattedCollectionProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  return formattedCollectionProduct
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    /**
     * Storefront API URL always uses .myshopify.com domain.
     */
    const urlPath =
      object.url.split(cnvs.store.localisedShopDomain)[1] ||
      object.url.split(cnvs.store.localisedPermanentDomain)[1]

    const isHomepage =
      object.url.replace('https:', '') === cnvs.store.localisedShopDomain ||
      object.url.replace('https:', '') === cnvs.store.localisedPermanentDomain

    const url = urlPath || isHomepage
      ? `//${location.host}${cnvs.routes.normalisedRoot}${urlPath ? urlPath : ''}`
      : object.url

    /**
     * Return formatted data.
     */
    return {
      handle,
      links,
      tier,
      title: object.title,
      url,
    }
  })
}

/**
 * Format Shopify image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  const imageSrc = src.src ? src.src : src

  if (!isCdnUrl(imageSrc)) {
    return imageSrc
  }

  const filetype = imageSrc.split('.').reverse()[0]?.split('?')[0]
  let version = imageSrc.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    return imageSrc
  }

  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = imageSrc
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(/(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g, '.')

  url = `//${url}${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Removes decimal places from number string.
 * @param {String} format - String Number.
 * @returns {String}
 */
export function formatNoDecimal(string) {
  return string.replace('.00', '')
}

/**
   * Format url to include market handle url.
   * @param {String} url - Url string.
   * @returns {String}
   */
export function formatUrl(url) {
  const pathname = new URL(url).pathname
  const primary = cnvs.strings.defaultLocalization.primaryMarket
  const country = cnvs.store.country.toLowerCase()
  const language = cnvs.store.language.toLowerCase()
  const isPrimaryDomain = primary.domain.toLowerCase() === country
  const isPrimaryLocale = primary.locale.toLowerCase() === language
  let updatedPathname = `/${language}-${country}${pathname}`

  if (isPrimaryDomain) {
    updatedPathname = updatedPathname.replace(`-${country}`, '')

    if (isPrimaryLocale) {
      updatedPathname = updatedPathname.replace(`/${language}`, '')
    }
  }

  return updatedPathname
}

/**
 * Formats product compare legend string.
 * @param {String} string - Legend string.
 * @returns {String}
 */
export function formatLegend(string) {
  if (!string.includes('(')) {
    return string
  }

  const splitString = string.split('(')
  return `<div>${splitString[0]}</div>
    <div class="text-body-s-regular mt-2xs">(${splitString[1]}</div>`
}
